/**
 * Rotating subtasks (ADR 0035): a repeating task done one part at a time — mirror of `parts.py`; both pass
 * `tests/vectors/parts.json`. The rules are in the Python module's docstring.
 */
export const ROTATE = "rec.rotateSubtasks";
export const PER_ROUND = "rec.perRound";
export const RANDOM_PEOPLE = "rec.randomPeople";
export const RANDOM_SUBTASKS = "rec.randomSubtasks";
/** the pairing tries every way to give the round's people its subtasks: a few people, a few subtasks */
export const MAX_PER_ROUND = 6;
const MAX_POOL = 8;

export interface Part {
  sub: string;
  who: string | null;
  done: boolean;
  by: string | null;
}

export interface Round {
  round: number;
  parts: Part[];
}

type TaskRec = { id?: string; props?: Record<string, unknown> };
type Entry = { sub: string; round: number; of?: unknown; for?: unknown; by?: unknown };

const props = (t: TaskRec) => (t.props && typeof t.props === "object" ? t.props : {});
/** as `rotationOf` in circle.ts, which reads this module for `mine` */
function rotation(task: TaskRec, members?: string[] | null): string[] {
  const rot = props(task)["rec.rotation"];
  const out = Array.isArray(rot) ? rot.map(String) : [];
  return members ? out.filter((x) => members.includes(x)) : out;
}
const isNum = (x: unknown): x is number => typeof x === "number" && Number.isFinite(x);

export const rotates = (task: TaskRec): boolean => props(task)[ROTATE] === true;

export function perRound(task: TaskRec): number {
  const n = props(task)[PER_ROUND];
  return isNum(n) ? Math.max(1, Math.min(MAX_PER_ROUND, Math.trunc(n))) : 1;
}

/** The seeded draw, the same in both languages: 32-bit FNV-1a, then MurmurHash3's finaliser (see `parts.py`). */
export function draw(s: string): number {
  let h = 0x811c9dc5;
  for (const b of new TextEncoder().encode(s)) {
    h ^= b;
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b) >>> 0;
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35) >>> 0;
  h ^= h >>> 16;
  return h >>> 0;
}

function entries(task: TaskRec): Entry[] {
  const hist = props(task)["rec.history"];
  if (!Array.isArray(hist)) return [];
  return hist.filter(
    (e): e is Entry =>
      !!e && typeof e === "object" && !e.skipped && typeof e.sub === "string" && isNum(e.round),
  );
}

/** One after the last round whose `of` parts are all done; 0 before the first. */
export function currentRound(es: Entry[]): number {
  const subs = new Map<number, Set<string>>();
  const of = new Map<number, number>();
  for (const e of es) {
    const r = Math.trunc(e.round);
    if (!subs.has(r)) subs.set(r, new Set());
    subs.get(r)!.add(e.sub);
    of.set(r, isNum(e.of) && e.of >= 1 ? Math.trunc(e.of) : 1);
  }
  let best = -1;
  for (const [r, s] of subs) if (s.size >= of.get(r)! && r > best) best = r;
  return best + 1;
}

/** Compare key tuples as Python does. */
const cmp = (a: number[], b: number[]): number => {
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return a[i] - b[i];
  return 0;
};

/** k-permutations of 0..n-1 in lexicographic order, as `itertools.permutations(range(n), k)`. */
function* permutations(n: number, k: number, prefix: number[] = []): Generator<number[]> {
  if (prefix.length === k) {
    yield prefix;
    return;
  }
  for (let i = 0; i < n; i++) if (!prefix.includes(i)) yield* permutations(n, k, [...prefix, i]);
}

/** What *at random* means in a workspace (ADR 0037): `even` (the counts first) or `chance` (a fresh draw each round). */
export interface Fairness {
  people: "even" | "chance";
  subtasks: "even" | "chance";
}

export function fairnessOf(settings: { fairness?: unknown } | null | undefined): Fairness {
  const raw = (settings?.fairness ?? {}) as Record<string, unknown>;
  return {
    people: raw.people === "chance" ? "chance" : "even",
    subtasks: raw.subtasks === "chance" ? "chance" : "even",
  };
}

/**
 * The current round of a task with rotating subtasks; null when it has none. `subs` are its open subtasks in order;
 * `fair` is the workspace's `fairnessOf`.
 */
export function roundOf(
  task: TaskRec,
  subs: string[],
  opts: { shared: boolean; members?: string[] | null; fair?: Fairness | null },
): Round | null {
  if (!rotates(task) || !subs.length) return null;
  const people = opts.shared ? rotation(task, opts.members) : [];
  const es = entries(task);
  const r = currentRound(es);
  const past = es.filter((e) => Math.trunc(e.round) < r);
  const seed = `${task.id ?? ""}:${r}`;
  const p = props(task);

  const turns = new Map<string, number>();
  const lastP = new Map<string, number>();
  const firstP = new Map<string, number>();
  const pair = new Map<string, number>();
  const subCount = new Map<string, number>();
  const lastS = new Map<string, number>();
  const pk = (who: string, s: string) => `${who}\u0000${s}`;
  for (const e of past) {
    const er = Math.trunc(e.round);
    subCount.set(e.sub, (subCount.get(e.sub) ?? 0) + 1);
    lastS.set(e.sub, Math.max(lastS.get(e.sub) ?? -1, er));
    if (typeof e.for === "string") {
      turns.set(e.for, (turns.get(e.for) ?? 0) + 1);
      lastP.set(e.for, Math.max(lastP.get(e.for) ?? -1, er));
      firstP.set(e.for, Math.min(firstP.get(e.for) ?? er, er));
      pair.set(pk(e.for, e.sub), (pair.get(pk(e.for, e.sub)) ?? 0) + 1);
    }
  }
  const randomP = p[RANDOM_PEOPLE] === true;
  const randomS = p[RANDOM_SUBTASKS] === true;
  // pure chance (ADR 0037): where the task draws, the draw alone decides; it evens out over many rounds
  const chanceP = randomP && opts.fair?.people === "chance";
  const chanceS = randomS && opts.fair?.subtasks === "chance";
  const key = (on: boolean, kind: string, items: string[]) =>
    new Map(items.map((x, i) => [x, on ? draw(`${seed}|${kind}|${x}`) : i]));
  // in order, the longest ago comes first; drawn, the draw decides among the equally often (else every round would
  // repeat the order of the first)
  const sinceS = (s: string) => (randomS ? 0 : (lastS.get(s) ?? -1));
  const keyS = key(randomS, "s", subs);
  const subRank = (s: string) => [subCount.get(s) ?? 0, sinceS(s), keyS.get(s)!];
  if (!people.length) {
    const best = chanceS
      ? [...subs].sort((a, b) => keyS.get(a)! - keyS.get(b)!)[0]
      : [...subs].sort((a, b) => cmp(subRank(a), subRank(b)))[0];
    return { round: r, parts: mark([{ sub: best, who: null, done: false, by: null }], es, r) };
  }

  const k = Math.min(perRound(task), people.length, subs.length);
  const keyP = key(randomP, "p", people);
  const turnsBefore = (q: string, rnd: number) =>
    past.filter((e) => e.for === q && Math.trunc(e.round) < rnd).length;
  const effective = (x: string) => {
    // someone who joins later starts one turn below the others, so they get one turn soon and no run of turns
    const joined = firstP.get(x) ?? r;
    const others = people.filter((q) => q !== x).map((q) => turnsBefore(q, joined));
    return (turns.get(x) ?? 0) + (others.length ? Math.max(0, Math.min(...others) - 1) : 0);
  };
  const personRank = (x: string) => [effective(x), randomP ? 0 : (lastP.get(x) ?? -1), keyP.get(x)!];
  const byKeyP = (a: string, b: string) => keyP.get(a)! - keyP.get(b)!;
  const chosen = chanceP
    ? [...people].sort(byKeyP).slice(0, k)
    : [...people].sort((a, b) => cmp(personRank(a), personRank(b))).slice(0, k);
  chosen.sort(byKeyP);
  if (chanceS) {
    // the first k of the drawn order, to the people in their order: every pairing is as likely as any other
    const drawn = [...subs].sort((a, b) => keyS.get(a)! - keyS.get(b)!).slice(0, k);
    const parts = drawn.map((sub, i) => ({ sub, who: chosen[i], done: false, by: null }));
    return { round: r, parts: mark(parts, es, r) };
  }
  // the subtasks to choose from: all of them, or the eight least often done (8 of them, 6 people: 20160 tries)
  const pool = [...subs].sort((a, b) => cmp(subRank(a), subRank(b))).slice(0, MAX_POOL);
  pool.sort((a, b) => keyS.get(a)! - keyS.get(b)!);
  let best: number[] | null = null;
  let bestOrder: number[] = [];
  for (const order of permutations(pool.length, k)) {
    // lexicographic: the first of the cheapest wins
    const cost = [
      order.reduce((n, j, i) => n + (pair.get(pk(chosen[i], pool[j])) ?? 0), 0),
      order.reduce((n, j) => n + (subCount.get(pool[j]) ?? 0), 0),
      order.reduce((n, j) => n + sinceS(pool[j]), 0),
    ];
    if (best === null || cmp(cost, best) < 0) {
      best = cost;
      bestOrder = order;
    }
  }
  const parts = bestOrder.map((j, i) => ({ sub: pool[j], who: chosen[i], done: false, by: null }));
  return { round: r, parts: mark(parts, es, r) };
}

function mark(parts: Part[], es: Entry[], r: number): Part[] {
  for (const e of es) {
    if (Math.trunc(e.round) !== r) continue;
    for (const part of parts) {
      if (part.sub === e.sub && !part.done) {
        part.done = true;
        part.by = typeof e.by === "string" ? (e.by.startsWith("profile:") ? e.by.slice(8) : e.by) : null;
      }
    }
  }
  return parts;
}

/** The open part of this person in the round; for a round without people, its open part. */
export function partFor(round: Round | null, profile: string | null): Part | null {
  if (!round) return null;
  return round.parts.find((x) => !x.done && (x.who === null || x.who === profile)) ?? null;
}

/** The part a tick on the task does: the one named, else this person's, else the first open one. */
export function pickPart(round: Round, profile: string | null, sub?: string | null): Part | null {
  const open = round.parts.filter((x) => !x.done);
  if (sub != null) return open.find((x) => x.sub === sub) ?? null;
  return partFor(round, profile) ?? open[0] ?? null;
}

/** The history entry for a part done. */
export function partEntry(round: Round, part: Part, at: string, by?: string | null): Record<string, unknown> {
  return {
    at,
    sub: part.sub,
    round: round.round,
    of: round.parts.length,
    ...(by ? { by } : {}),
    ...(part.who ? { for: part.who } : {}),
  };
}
